/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 * Cashback Reserve Modal initial component
 *
 * @author Yakov Gushchin
 * @author Julia Ignatieva
 * @version 1.14.0
 * @since 1.12.0
 */

import { Fragment, useCallback, useEffect, useState } from 'react';

import { Checkbox } from '../../../../../components/common';
import { MoneyInput } from '../../../../../components/common/MoneyInput/MoneyInput';
import { parseMoneyToFloat } from '../../../../../components/common/MoneyInput/utils/parseMoneyToFloat';
import GTMOperations from '../../../../../modules/analytics/GoogleTagManager/GTMOperations';
import { IGTMPushAnalyticPayload } from '../../../../../modules/analytics/GoogleTagManager/types/GTMActionTypes';
import Modal from '../../../../../modules/modal/components/Modal';
import ButtonServiceUserDenied from '../../../../../modules/role/components/ButtonServiceUserDenied';
import declination from '../../../../../utils/declination';
import { formatBalanceWithoutTrailingZeros } from '../../../../../utils/formatText';
import { stringFormatParams } from '../../../../../utils/stringFormat';
import { CashbackReserveModalBodyStyled } from '../../styled/CashbackReserveModalStyled';
import { ICashbackReserveModal } from './CashbackReserveModal';

export interface ICashbackReserveModalStart extends ICashbackReserveModal {
    pushGtmAnalytic: (payload: IGTMPushAnalyticPayload) => void;
    reserve: (score: number, isReserveAll: boolean) => void;
}

const CashbackReserveModalStart = (props: ICashbackReserveModalStart) => {

    const {
        maxScore,
        service,
        translation,
        translationCommon,

        pushGtmAnalytic,
        reserve,
    } = props;

    const [score, setScore] = useState<string>();
    const [isScoreValid, setScoreValid] = useState<boolean>(true);
    const [isReserveAllChecked, setReserveAllChecked] = useState<boolean>(false);

    useEffect(() => {
        pushGtmAnalytic({
            operation: GTMOperations.cashback.openPopUpCashbackPage(
                stringFormatParams(translation.start.title, { name: service.title }),
            ),
            withAccountType: true,
            withSusrId: true,
            withUserRole: true,
        });
    }, [pushGtmAnalytic, service.title, translation.start.title]);

    const reserveAllCheckboxLabel = stringFormatParams(translation.start.checkboxReserveAll, {
        amount: `${formatBalanceWithoutTrailingZeros(maxScore)} ${declination(maxScore, translationCommon.scores)}`,
    });

    const title = stringFormatParams(translation.start.title, { name: service.title });
    /*
    * -------------------------------------------
    *         Handlers
    * -------------------------------------------
    */

    const onScoreChange = useCallback((score: string) => {
        setScore(score);
        setScoreValid(!!score && score !== '0' && parseMoneyToFloat(score) <= maxScore);
    }, [maxScore]);

    const onReserveAllChecked = useCallback((isReserveAll: boolean) => {
        pushGtmAnalytic({
            operation: GTMOperations.cashback.clickOnReserveAllCashback(title, isReserveAll),
            withAccountType: true,
            withSusrId: true,
            withUserRole: true,
        });
        setReserveAllChecked(isReserveAll);
        setScore(maxScore.toString());
        setScoreValid(true);
    }, [maxScore, pushGtmAnalytic, title]);

    const onReserve = useCallback(() => {
        if (!score) return;

        reserve(parseMoneyToFloat(score), isReserveAllChecked);
    }, [isReserveAllChecked, reserve, score]);

    return (
        <Fragment>
            <Modal.Title>{title}</Modal.Title>
            <Modal.Text>{translation.start.subTitle}</Modal.Text>

            <CashbackReserveModalBodyStyled>
                <MoneyInput
                    autoFocus={true}
                    className="cashback-reserve-modal__score-input"
                    disabled={isReserveAllChecked}
                    isValid={isScoreValid}
                    moneyPlaceholder={translation.start.inputPlaceholder}
                    value={score || ''}
                    onMoneyInputChange={onScoreChange}
                />
                <Checkbox
                    checked={isReserveAllChecked}
                    className="cashback-reserve-modal__reserve-checkbox"
                    label={reserveAllCheckboxLabel}
                    onChange={onReserveAllChecked}
                />
            </CashbackReserveModalBodyStyled>
            <Modal.Control>
                <ButtonServiceUserDenied
                    disabled={!isScoreValid || !score}
                    kind="medium"
                    onClick={onReserve}
                >
                    {translation.start.btnReserve}
                </ButtonServiceUserDenied>
            </Modal.Control>
        </Fragment>
    );
};

export default CashbackReserveModalStart;
